import React, { useState, useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import { cn } from "../lib/utils";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Region,
  fetchAllRegions,
  fetchEnvironmentalData,
} from "../services/environmentalDataService";
import { useToast } from "../hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useTheme } from "../context/ThemeContext";
import {
  TreePine,
  Mountain,
  Waves,
  Shield,
  Info,
  Maximize2,
  RotateCcw,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

// Add type definition for import.meta.env
declare global {
  interface ImportMetaEnv {
    VITE_MAPBOX_ACCESS_TOKEN: string;
  }
}

interface RegionHeatmapProps {
  onRegionSelect: (regionId: string) => void;
  selectedRegion: string | null;
}

type HeatmapDataType =
  | "deforestation"
  | "desertification"
  | "landLoss"
  | "endangeredSpecies";

const RegionHeatmap: React.FC<RegionHeatmapProps> = ({
  onRegionSelect,
  selectedRegion,
}): JSX.Element => {
  const { toast } = useToast();
  const { theme } = useTheme();
  const mapToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [dataType, setDataType] = useState<HeatmapDataType>("deforestation");
  const [regions, setRegions] = useState<Region[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredRegion, setHoveredRegion] = useState<Region | null>(null);
  const [showLegend, setShowLegend] = useState(true);
  const [dataStats, setDataStats] = useState<{
    min: number;
    max: number;
    avg: number;
  } | null>(null);

  // Separate data loading and map initialization
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        const regionsData = await fetchAllRegions();
        setRegions(regionsData);

        // Initialize map after regions are loaded
        if (!map.current && mapContainer.current && regionsData.length > 0) {
          mapboxgl.accessToken = mapToken;

          map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style:
              theme === "light"
                ? "mapbox://styles/mapbox/light-v11"
                : "mapbox://styles/mapbox/dark-v11",
            center: [0, 20],
            zoom: 1.5,
            projection: "mercator",
          });

          map.current.addControl(
            new mapboxgl.NavigationControl({
              visualizePitch: true,
            }),
            "top-right",
          );

          map.current.on("load", () => {
            setMapLoaded(true);
          });
        }
      } catch (error) {
        console.error("Failed to load initial data:", error);
        toast({
          title: "Error loading data",
          description: "Could not load region data. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [mapToken, toast, theme]);

  // Update map style when theme changes
  useEffect(() => {
    if (map.current && mapLoaded) {
      map.current.setStyle(
        theme === "light"
          ? "mapbox://styles/mapbox/light-v11"
          : "mapbox://styles/mapbox/dark-v11",
      );
    }
  }, [theme, mapLoaded]);

  // Handle data updates and heatmap layer
  useEffect(() => {
    const updateHeatmapData = async () => {
      if (!map.current || !mapLoaded || regions.length === 0) return;

      try {
        const features = await Promise.all(
          regions.map(async (region) => {
            const envData = await fetchEnvironmentalData(region.id);
            if (!envData) return null;

            let value = 0;
            switch (dataType) {
              case "deforestation":
                value = envData.deforestation.ratePerYear * 10; // Convert percentage to a 0-10 scale
                break;
              case "desertification":
                value = envData.desertification.ratePerYear * 10; // Convert percentage to a 0-10 scale
                break;
              case "landLoss":
                value = envData.landLoss.ratePerYear * 10; // Convert percentage to a 0-10 scale
                break;
              case "endangeredSpecies":
                // For species count, we'll use a logarithmic scale and multiply by 4 for more intensity
                value = Math.log10(envData.endangeredSpecies.count + 1) * 4;
                break;
            }

            return {
              type: "Feature",
              properties: {
                value,
                regionId: region.id,
                name: region.name,
              },
              geometry: {
                type: "Point",
                coordinates: region.coordinates,
              },
            };
          }),
        );

        // Filter out null values and create the GeoJSON object
        const validFeatures = features.filter(Boolean);
        const geojson = {
          type: "FeatureCollection",
          features: validFeatures,
        };

        // Calculate statistics for the legend
        const values = validFeatures.map((f) => f.properties.value);
        if (values.length > 0) {
          setDataStats({
            min: Math.min(...values),
            max: Math.max(...values),
            avg: values.reduce((a, b) => a + b, 0) / values.length,
          });
        }

        // Remove existing layers if they exist
        if (map.current.getSource("environmental-data")) {
          map.current.removeLayer("heatmap-layer");
          map.current.removeSource("environmental-data");
        }

        // Add the new data source and heatmap layer
        map.current.addSource("environmental-data", {
          type: "geojson",
          data: geojson as any,
        });

        // Define color schemes for light and dark themes
        const lightThemeColors = [
          "rgba(255, 255, 255, 0)",
          "rgba(255, 255, 0, 0.2)",
          "rgba(255, 165, 0, 0.4)",
          "rgba(255, 69, 0, 0.6)",
          "rgba(255, 0, 0, 0.8)",
          "rgba(255, 0, 0, 1)",
        ];

        const darkThemeColors = [
          "rgba(0, 0, 0, 0)",
          "rgba(255, 255, 0, 0.2)",
          "rgba(255, 165, 0, 0.4)",
          "rgba(255, 69, 0, 0.6)",
          "rgba(255, 0, 0, 0.8)",
          "rgba(255, 0, 0, 1)",
        ];

        // Use the appropriate color scheme based on the current theme
        const heatmapColors =
          theme === "light" ? lightThemeColors : darkThemeColors;

        map.current.addLayer({
          id: "heatmap-layer",
          type: "heatmap",
          source: "environmental-data",
          paint: {
            "heatmap-weight": [
              "interpolate",
              ["linear"],
              ["get", "value"],
              0,
              0,
              20,
              2,
            ],
            "heatmap-intensity": [
              "interpolate",
              ["linear"],
              ["zoom"],
              0,
              1,
              9,
              3,
            ],
            "heatmap-color": [
              "interpolate",
              ["linear"],
              ["heatmap-density"],
              0,
              heatmapColors[0],
              0.2,
              heatmapColors[1],
              0.4,
              heatmapColors[2],
              0.6,
              heatmapColors[3],
              0.8,
              heatmapColors[4],
              1,
              heatmapColors[5],
            ],
            "heatmap-radius": [
              "interpolate",
              ["linear"],
              ["zoom"],
              0,
              20,
              9,
              50,
            ],
            "heatmap-opacity": theme === "light" ? 0.7 : 0.8,
          },
        });

        // Add click handler for region selection
        map.current.on("click", "heatmap-layer", (e) => {
          if (e.features && e.features[0]) {
            const feature = e.features[0];
            if (feature.properties?.regionId) {
              onRegionSelect(feature.properties.regionId);
            }
          }
        });

        // Add hover effects
        map.current.on("mousemove", "heatmap-layer", (e) => {
          if (e.features && e.features[0]) {
            map.current!.getCanvas().style.cursor = "pointer";
            const feature = e.features[0];
            const region = regions.find(
              (r) => r.id === feature.properties?.regionId,
            );
            if (region) {
              setHoveredRegion(region);
            }
          }
        });

        map.current.on("mouseleave", "heatmap-layer", () => {
          map.current!.getCanvas().style.cursor = "";
          setHoveredRegion(null);
        });
      } catch (error) {
        console.error("Error updating heatmap data:", error);
      }
    };

    updateHeatmapData();
  }, [regions, mapLoaded, dataType, onRegionSelect, theme]);

  // Handle selected region changes
  useEffect(() => {
    if (!map.current || !selectedRegion) return;

    const region = regions.find((r) => r.id === selectedRegion);
    if (region) {
      map.current.flyTo({
        center: region.coordinates as [number, number],
        zoom: 4,
        duration: 1500,
        essential: true,
      });
    }
  }, [selectedRegion, regions]);

  return (
    <div className="relative w-full h-full overflow-hidden rounded-xl border border-border bg-primary/5 dark:bg-[#272727]">
      {isLoading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/95 backdrop-blur-sm z-10">
          <div className="relative mb-4">
            <div className="w-12 h-12 border-4 border-[#40e0d0]/20 border-t-[#40e0d0] rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Mountain className="w-6 h-6 text-[#40e0d0] animate-pulse" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-foreground mb-2">
              Loading Environmental Data
            </p>
            <p className="text-sm text-muted-foreground">
              Analyzing regional environmental patterns...
            </p>
          </div>
        </div>
      )}

      {/* Modern Data Type Selector */}
      <div className="absolute top-4 left-4 z-10 space-y-2">
        <div className="bg-background/95 backdrop-blur-md border border-border rounded-xl p-3 shadow-lg">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 bg-[#40e0d0] rounded-full animate-pulse"></div>
            <span className="text-xs font-medium text-foreground">
              Environmental Data
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              {
                id: "deforestation",
                label: "Forest Loss",
                icon: TreePine,
                color: "text-green-600 dark:text-green-400",
              },
              {
                id: "desertification",
                label: "Desertification",
                icon: Mountain,
                color: "text-orange-600 dark:text-orange-400",
              },
              {
                id: "landLoss",
                label: "Land Loss",
                icon: Waves,
                color: "text-blue-600 dark:text-blue-400",
              },
              {
                id: "endangeredSpecies",
                label: "Species Risk",
                icon: Shield,
                color: "text-red-600 dark:text-red-400",
              },
            ].map(({ id, label, icon: Icon, color }) => (
              <button
                key={id}
                onClick={() => setDataType(id as HeatmapDataType)}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-200 ${
                  dataType === id
                    ? "bg-[#40e0d0]/10 border border-[#40e0d0]/30 shadow-sm"
                    : "hover:bg-muted/50 border border-transparent"
                }`}
              >
                <Icon
                  className={`w-4 h-4 ${dataType === id ? "text-[#40e0d0]" : color}`}
                />
                <span
                  className={`text-xs font-medium ${dataType === id ? "text-[#40e0d0]" : "text-foreground/70"}`}
                >
                  {label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Map Controls */}
        <div className="bg-background/95 backdrop-blur-md border border-border rounded-xl p-2 shadow-lg">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => map.current?.flyTo({ center: [0, 20], zoom: 1.5 })}
              className="w-8 h-8 p-0 hover:bg-[#40e0d0]/10"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowLegend(!showLegend)}
              className="w-8 h-8 p-0 hover:bg-[#40e0d0]/10"
            >
              <Info className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div ref={mapContainer} className="w-full h-full" />

      {hoveredRegion && (
        <div className="absolute z-50 bg-background/95 backdrop-blur-md border border-border rounded-xl shadow-xl p-4 transform -translate-x-1/2 left-1/2 bottom-4 min-w-[200px]">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 bg-gradient-to-r from-[#40e0d0] to-emerald-500 rounded-full"></div>
            <h3 className="font-semibold text-foreground">
              {hoveredRegion.name}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground mb-2">
            {hoveredRegion.country}
          </p>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Click to explore data</span>
            <Badge variant="outline" className="text-xs">
              {dataType === "deforestation" && "Forest"}
              {dataType === "desertification" && "Desert"}
              {dataType === "landLoss" && "Land"}
              {dataType === "endangeredSpecies" && "Species"}
            </Badge>
          </div>
        </div>
      )}

      {/* Enhanced Legend and Info Panel */}
      {showLegend && (
        <div className="absolute bottom-4 right-4 bg-background/95 backdrop-blur-md border border-border rounded-xl shadow-lg p-4 max-w-[280px]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-red-500 rounded-full"></div>
              <span className="text-sm font-semibold text-foreground">
                Intensity Legend
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowLegend(false)}
              className="w-6 h-6 p-0 hover:bg-muted/50"
            >
              ×
            </Button>
          </div>

          {/* Color Scale */}
          <div className="space-y-2 mb-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Low</span>
              <span>High</span>
            </div>
            <div className="h-3 rounded-full bg-gradient-to-r from-transparent via-yellow-400 to-red-500 border border-border"></div>
          </div>

          {/* Data Type Description */}
          <div className="text-xs text-foreground/80 leading-relaxed">
            {dataType === "deforestation" && (
              <p>
                <strong>Deforestation:</strong> Annual forest loss rates
                highlighting critical deforestation zones planetwide.
              </p>
            )}
            {dataType === "desertification" && (
              <p>
                <strong>Desertification:</strong> Land degradation patterns
                showing areas at risk of becoming desert.
              </p>
            )}
            {dataType === "landLoss" && (
              <p>
                <strong>Land Loss:</strong> Coastal erosion and land degradation
                affecting habitable areas.
              </p>
            )}
            {dataType === "endangeredSpecies" && (
              <p>
                <strong>Species Risk:</strong> Biodiversity hotspots with
                highest concentration of endangered species.
              </p>
            )}
          </div>

          {/* Statistics */}
          {dataStats && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="text-center">
                  <div className="font-medium text-foreground">
                    {dataStats.min.toFixed(1)}
                  </div>
                  <div className="text-muted-foreground">Min</div>
                </div>
                <div className="text-center">
                  <div className="font-medium text-foreground">
                    {dataStats.avg.toFixed(1)}
                  </div>
                  <div className="text-muted-foreground">Avg</div>
                </div>
                <div className="text-center">
                  <div className="font-medium text-foreground">
                    {dataStats.max.toFixed(1)}
                  </div>
                  <div className="text-muted-foreground">Max</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RegionHeatmap;
